import { SpinResponse, SpecialSymbolGroupType } from '@cgs/data-access';
import {
  ComponentIndex,
  ComponentNames,
  GAME_STATE_MACHINE_PROVIDER_TOKEN,
  GameStateMachine,
  IGameStateMachineProvider,
  ISlotGameEngineProvider,
  MultiSceneReelsEngineV2,
  RegularSpinsSoundModel,
  RegularSpinsSoundModelComponent,
  T_ISlotGameEngineProvider,
  T_RegularSpinsSoundModelComponent,
} from '@cgs/machines';
import {
  Container,
  EmptyAction,
  FunctionAction,
  IntervalAction,
  ParallelAction,
  SceneObject,
} from '@cgs/wge';
import { FreeSpinEventType } from '../free-spins/corsica-free-spins.types';
import { createIconValueDescription } from '../free-spins/corsica-icon-with-values/corsica-icon-values.helper';

export const CORSICA_BONUS_TRIGGER_COMPONENT = Symbol('CorsicaBonusTriggerComponent');

export class CorsicaBonusTriggerComponent {
  private readonly gameStateMachine: GameStateMachine<SpinResponse>;
  private readonly reelsEngine: MultiSceneReelsEngineV2;

  private readonly reelSoundModel: RegularSpinsSoundModel;

  private readonly iconNodeComponentIndex: ComponentIndex;

  constructor(container: Container) {
    this.gameStateMachine = container.resolve<IGameStateMachineProvider>(
      GAME_STATE_MACHINE_PROVIDER_TOKEN
    ).gameStateMachine;

    this.reelsEngine = container.resolve<ISlotGameEngineProvider>(T_ISlotGameEngineProvider)
      .reelsEngine as MultiSceneReelsEngineV2;

    this.iconNodeComponentIndex = this.reelsEngine.entityEngine.getComponentIndex(
      ComponentNames.IconNode
    );

    this.reelSoundModel = container.resolve<RegularSpinsSoundModelComponent>(
      T_RegularSpinsSoundModelComponent
    ).regularSpinSoundModel;

    this.gameStateMachine.shortWinLines.appendLazyAnimation(() => this.checkTriggerBonus());
  }

  private checkTriggerBonus(): IntervalAction {
    if (
      this.gameStateMachine.curResponse.bonusInfo === null ||
      !this.gameStateMachine.curResponse.bonusInfo.bonusStarted
    ) {
      return new EmptyAction();
    }

    const specGroups = this.gameStateMachine.curResponse.specialSymbolGroups || [];

    const animations: IntervalAction[] = [new EmptyAction()];

    specGroups
      .filter(
        (group) =>
          group.type === SpecialSymbolGroupType.Multiplier &&
          group.subType === FreeSpinEventType.Init
      )
      .forEach((group) => {
        if (!group.positions?.length) {
          return;
        }

        const symbolPosition = group.positions[0];

        const reel = this.reelsEngine.getReelByPosition(symbolPosition);
        const line = this.reelsEngine.getLineByPosition(symbolPosition);

        const entity = this.reelsEngine.getStoppedEntities(reel, line)[0];

        if (!entity) {
          return;
        }

        const iconNode = entity.get<SceneObject>(this.iconNodeComponentIndex);

        const valueDescription = createIconValueDescription(
          group.totalWin!,
          symbolPosition,
          'multText'
        );

        if (!iconNode) {
          return;
        }

        const bonusSound = this.reelSoundModel.getSoundByName('bonus3_line');

        animations.push(
          ...[
            new FunctionAction(() => {
              bonusSound.GoToState('stop');
              entity.addComponent(ComponentNames.IconValue, valueDescription);
              entity.addComponent(ComponentNames.NeedUpdate, true);
              this.reelsEngine.startAnimation(entity, 'anim');
              bonusSound.GoToState('play');
            }),
            new EmptyAction().withDuration(iconNode.stateMachine?.getStateDuration('anim') || 0),
            new FunctionAction(() => {
              this.reelsEngine.startAnimation(entity, 'stop');
            }),
          ]
        );
      });

    return new ParallelAction(animations);
  }
}
